import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon, ChevronDown, Search } from "lucide-react";

import { I18nKeyType } from "@libs/i18n";
import {
  RmCommandEmpty,
  RmCommandGroup,
  RmCommandInput,
  RmCommandItem,
  RmCommandList,
  RmCommandLoading,
} from "@/components/RmCommand";
import {
  RmPopover,
  RmPopoverContent,
  RmPopoverTrigger,
} from "@/components/RmPopover";
import { NumberOptionType, OptionType } from "@/domain/common";
import { useTranslate } from "@/hooks/useTranslate";
import { cn } from "@/utils/tailwind";

export type BaseTextSelectPropsType = {
  value?: string | number;
  onValueChange?: (value?: OptionType | NumberOptionType) => void;
  emptyI18nKey?: I18nKeyType;
  emptyI18nOptions?: object;
  isLoading?: boolean;
  isDisabled?: boolean;
  placeholderI18nKey?: I18nKeyType;
  placeholderI18nOptions?: object;
  isUsingAsyncData?: boolean;
  showListWhenEmpty?: boolean;
  onInputChange?: (value: string) => void;
  isInputReadOnly?: boolean;
  shouldAutoSelectOptionOnInputChange?: boolean;
  isClearable?: boolean;
} & (
  | {
      isNumberValue?: false | undefined;
      options: OptionType[];
    }
  | {
      isNumberValue: true;
      options: NumberOptionType[];
    }
);

export const BaseTextSelect: FC<BaseTextSelectPropsType> = ({
  options,
  placeholderI18nKey,
  placeholderI18nOptions,
  emptyI18nKey,
  emptyI18nOptions,
  value,
  onValueChange,
  onInputChange,
  isDisabled,
  isLoading = false,
  isUsingAsyncData,
  showListWhenEmpty = false,
  isInputReadOnly,
  shouldAutoSelectOptionOnInputChange = true,
  isClearable = true,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    OptionType | NumberOptionType | undefined
  >(options.find(option => option.value === value));
  const [inputValue, setInputValue] = useState<string>(
    options.find(option => option.value === value)?.label ?? "",
  );

  const placeholder = useTranslate(placeholderI18nKey, placeholderI18nOptions);
  const emptyMessage = useTranslate(
    emptyI18nKey ?? "common:noResultsFound",
    emptyI18nOptions,
  );

  // Handle the case when the value is changed from the outside, like form.setValue
  useEffect(() => {
    const option = options.find(option => option.value === value);
    setSelectedOption(option);
    setInputValue(option?.label ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setIsOpen(true);
      }

      // Select the option if the user presses Enter
      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          option => option.label === input.value,
        );
        if (optionToSelect) {
          setSelectedOption(optionToSelect);
          onValueChange?.(optionToSelect);
        }
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === "Escape") {
        input.blur();
      }
    },
    [isOpen, options, onValueChange],
  );

  const handleBlur = useCallback(() => {
    setIsOpen(false);
    if (!selectedOption) {
      setInputValue("");
      onValueChange?.(selectedOption);
    } else {
      setInputValue(selectedOption.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  const handleSelectOption = useCallback(
    (selectedOptionOption: OptionType | NumberOptionType) => {
      setInputValue(selectedOptionOption.label);

      setSelectedOption(selectedOptionOption);
      onValueChange?.(selectedOptionOption);

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur();
      }, 0);
    },
    [onValueChange],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      onInputChange?.(value);
      setInputValue(value);
      if (shouldAutoSelectOptionOnInputChange || !value) {
        const newOption = options.find(option => option.label === value);

        if (!newOption && !isClearable) {
          return;
        }
        setSelectedOption(newOption);
      }
    },
    [onInputChange, options, shouldAutoSelectOptionOnInputChange, isClearable],
  );

  const shouldShowList = isUsingAsyncData
    ? Boolean(inputValue) || showListWhenEmpty
    : true;

  return (
    <RmPopover open={isOpen}>
      <CommandPrimitive
        shouldFilter={!isUsingAsyncData}
        className="overflow-visible rounded-md bg-background">
        <RmPopoverTrigger asChild>
          <div onClick={() => !isDisabled && setIsOpen(true)}>
            <RmCommandInput
              ref={inputRef}
              value={inputValue}
              onValueChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={isDisabled}
              readOnly={isInputReadOnly}
              leftElement={
                isUsingAsyncData ? (
                  <Search className="size-4 shrink-0" />
                ) : undefined
              }
              rightElement={
                isUsingAsyncData ? undefined : (
                  <ChevronDown className="size-4 shrink-0" />
                )
              }
            />
          </div>
        </RmPopoverTrigger>
        <RmPopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          hidden={!shouldShowList}
          onOpenAutoFocus={e => e.preventDefault()}>
          <RmCommandList>
            {isLoading ? (
              <RmCommandLoading />
            ) : (
              <RmCommandGroup>
                {options.map(option => (
                  <RmCommandItem
                    key={option.value}
                    value={option.label}
                    disabled={option.isDisabled}
                    onMouseDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onSelect={() => handleSelectOption(option)}
                    className={cn("flex w-full items-center")}>
                    <span className="truncate pr-4">{option.label}</span>
                    {selectedOption?.value === option.value && (
                      <div className="absolute top-0 right-2 bottom-0 flex w-4 items-center justify-center">
                        <CheckIcon className="size-4 shrink-0" />
                      </div>
                    )}
                  </RmCommandItem>
                ))}
              </RmCommandGroup>
            )}
            {!isLoading ? (
              <RmCommandEmpty>{emptyMessage}</RmCommandEmpty>
            ) : null}
          </RmCommandList>
        </RmPopoverContent>
      </CommandPrimitive>
    </RmPopover>
  );
};
